/**
 * 画面単位のエラー境界（SRE 画面側 3）。
 * 想定外の例外でアプリ全体を白くしない。その画面の部分に「読み込めませんでした（再読み込み）」を出し、
 * /api/client-events に送る（メッセージと画面名だけ。入力値は送らない）。
 * 分割した画面の読み込み失敗（デプロイで古いファイルが消えた）は「新しい版があります」の案内にする。
 */
import { Component, type ReactNode } from "react";
import { announceNewVersion } from "../api.js";
import { sendClientEvent, currentPage, errorMessage } from "../telemetry.js";

export function isChunkLoadError(err: unknown): boolean {
  const m = err instanceof Error ? `${err.name} ${err.message}` : String(err);
  return /dynamically imported module|Importing a module script failed|error loading dynamically|ChunkLoadError|Unable to preload CSS/i.test(m);
}

interface Props {
  /** 画面が変わったら境界を戻す。 */
  resetKey: string;
  fallback: (info: { chunk: boolean; retry: () => void }) => ReactNode;
  children: ReactNode;
}

export class ErrorBoundary extends Component<Props, { error: unknown; key: string }> {
  state = { error: null as unknown, key: this.props.resetKey };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: { error: unknown; key: string }) {
    if (props.resetKey !== state.key) return { error: null, key: props.resetKey };
    return null;
  }

  componentDidCatch(error: unknown) {
    if (isChunkLoadError(error)) {
      // 端末がオフラインのときは「新しい版」ではなく通信の失敗（案内は RouteError が出す）
      if (navigator.onLine !== false) announceNewVersion();
      return;
    }
    sendClientEvent({ kind: "error", page: currentPage(), message: errorMessage(error) });
  }

  render() {
    if (this.state.error) {
      return this.props.fallback({ chunk: isChunkLoadError(this.state.error), retry: () => this.setState({ error: null }) });
    }
    return this.props.children;
  }
}
